import { scanCategoryApiV1 as scanCategoryApi } from '@deepup/apis';
import { useScanIdsWithTexturedMeshInScope } from '@scans/components/Scans/hooks';
import type { QAState, SurfaceClassification } from '@scans/types';
import { qaStates, surfaceClassificationCategories } from '@scans/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import type { ScanStatsView } from '@viewer3D/redux/interface';
import type { BoundingBoxWithHeight } from '@viewer3D/types';
import { DateTime } from 'luxon';
import { useMemo, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import type { XOR } from 'ts-xor';
import { useScansInScope } from './menu/ScanOrganization';

export const useScanStats = (): ScanStatsView => {
  const visibility = useSelector(viewer3dSelectors.visibleScans);
  const scans = useScansInScope().filter((scan) => scan.selectedProcessingResultId);
  const visible =
    visibility === 'ALL' ? scans : visibility === 'NONE' ? [] : scans.filter((x) => x.visible);

  const scansIds = useScanIdsWithTexturedMeshInScope(visible);

  const loaded = scans.filter((s) => s.loaded).filter((s) => scansIds.includes(s.id));
  const loading = visible.filter((s) => !s.loaded).filter((s) => scansIds.includes(s.id));
  const loadedVisiblePercent = scansIds.length ? (loaded.length / scansIds.length) * 100 : 0;

  return {
    total: scans.length,
    hidden: scans.length - visible.length,
    loaded: loaded.length,
    loading: loading.length,
    loadedVisiblePercent,
  };
};

export const scanCategories = [
  { id: scanCategoryApi.ScanCategoryKind.CONSTRUCTION, name: 'Construction' },
  { id: scanCategoryApi.ScanCategoryKind.EVIDENCE_COLLECTION, name: 'Evidence Collection' },
  {
    id: scanCategoryApi.ScanCategoryKind.EVIDENCE_COLLECTION_BEFORE,
    name: 'Evidence Collection Before',
  },
  {
    id: scanCategoryApi.ScanCategoryKind.EVIDENCE_COLLECTION_AFTER,
    name: 'Evidence Collection After',
  },
  { id: scanCategoryApi.ScanCategoryKind.FORGOTTEN_SCAN, name: 'Forgotten Scan' },
  { id: scanCategoryApi.ScanCategoryKind.UNSPECIFIED, name: 'Unspecified' },
];

export type ScanCategories = keyof typeof scanCategoryApi.ScanCategoryKind;

// A Scan/Photo has two different dates: the date where it was recorded at and the date where it was uploaded
export type DateFilterType = 'recordedAt' | 'uploadedAt';

export type DateRange = { from: string; to: string; type: DateFilterType };

export type ScanFilters = {
  qaState?: QAState;
  // setters need to be exposed always in order to switch from viewport to date range or back
  setQaState: (qaState: QAState) => void;
  removeQaState: () => void;
  surfaceClassifications?: SurfaceClassification[];
  scanCategory?: scanCategoryApi.ScanCategoryKind;
  setScanCategory: (scanCategory: scanCategoryApi.ScanCategoryKind) => void;
  removeScanCategory: () => void;
  setSurfaceClassification: (surfaceClassifications: SurfaceClassification[]) => void;
  removeSurfaceClassification: () => void;
  setDateRange: (dateRange: DateRange) => void;
  setViewport: (viewport: BoundingBoxWithHeight) => void;
} & XOR<
  {
    dateRange: DateRange;
  },
  {
    viewport: BoundingBoxWithHeight;
  }
>;

type BaseParams = {
  qaState?: QAState;
  surfaceClassifications?: string;
};

type DateRangeParams =
  | {
      beginDateGte: string;
      beginDateLte: string;
    }
  | { uploadedAtGte: string; uploadedAtLte: string };

type ViewportParams = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

type FiltersRequestParams =
  BaseParams | (BaseParams & DateRangeParams) | (BaseParams & ViewportParams);

export const encodeScanFilters = ({
  qaState,
  viewport,
  dateRange,
  surfaceClassifications,
}: ScanFilters): FiltersRequestParams => {
  const isAllSurfaceClassificationsSelected =
    surfaceClassifications &&
    surfaceClassifications.length === surfaceClassificationCategories.length;

  const out: FiltersRequestParams = {
    qaState,
    surfaceClassifications: isAllSurfaceClassificationsSelected
      ? undefined
      : surfaceClassifications?.join(','),
  };

  if (viewport) {
    const { minX, maxX, minY, maxY } = viewport;
    return {
      ...out,
      minX,
      maxX,
      minY,
      maxY,
    };
  }

  if (dateRange) {
    const fromDate = DateTime.fromISO(dateRange.from, { zone: 'UTC+0' }).toISO()!;
    const toDate = DateTime.fromISO(dateRange.to, { zone: 'UTC+0' }).plus({ days: 1 }).toISO()!;
    return dateRange.type === 'recordedAt'
      ? {
          ...out,
          beginDateGte: fromDate,
          beginDateLte: toDate,
        }
      : {
          ...out,
          uploadedAtGte: fromDate,
          uploadedAtLte: toDate,
        };
  }

  return out;
};

const parseViewport = (viewportJson: string | null): BoundingBoxWithHeight | undefined => {
  if (!viewportJson) {
    return undefined;
  }
  try {
    const parsedViewport = JSON.parse(viewportJson) as BoundingBoxWithHeight;
    const containsBoundsAsNumbers = ['minX', 'maxX', 'minY', 'maxY', 'z'].every(
      (key) => typeof parsedViewport[key as keyof BoundingBoxWithHeight] === 'number',
    );
    return containsBoundsAsNumbers ? parsedViewport : undefined;
  } catch {
    return undefined;
  }
};

const parseDate = (date: string | null): string | undefined =>
  date && DateTime.fromISO(date).isValid ? date : undefined;

const parseDateRangeType = (dateRangeType: string | null): DateFilterType | undefined =>
  dateRangeType === 'recordedAt' || dateRangeType === 'uploadedAt' ? dateRangeType : undefined;

const parseDateRange = (
  from: string | null,
  to: string | null,
  type: string | null,
): DateRange | undefined =>
  parseDate(from) && parseDate(to) && parseDateRangeType(type)
    ? {
        from: from!,
        to: to!,
        type: type as DateFilterType,
      }
    : undefined;

const parseQAState = (qaState: string | null): QAState | undefined =>
  qaState && qaStates.includes(qaState as QAState) ? (qaState as QAState) : undefined;

const parseSurfaceClassification = (
  surfaceClassification: string | null,
): SurfaceClassification[] | undefined => {
  if (surfaceClassification === null) {
    return [...surfaceClassificationCategories];
  }
  if (surfaceClassification === '') {
    return [];
  }
  const surfaceClassificationsList = surfaceClassification.split(',') as SurfaceClassification[];
  const allCategoriesValid = surfaceClassificationsList.every((category) =>
    surfaceClassificationCategories.includes(category),
  );

  return allCategoriesValid ? surfaceClassificationsList : undefined;
};

const parseScanCategory = (categoryString: string | null) => {
  if (!categoryString) {
    return scanCategoryApi.ScanCategoryKind['UNSPECIFIED'];
  }

  return scanCategoryApi.ScanCategoryKind[categoryString as ScanCategories];
};

const today = DateTime.now();
// if OPs begin their work on monday, they need to see what happened on the weekend at the building site. Otherwise, they only need to see what happened yesterday.
export const getYesterdayOrWeekend = (today: DateTime) =>
  today.minus({ days: today.weekdayLong === 'Monday' ? 3 : 1 });

export const defaultDateRange: DateRange & { type: DateFilterType } = {
  from: getYesterdayOrWeekend(today).toISODate()!,
  to: today.toISODate(),
  type: 'recordedAt',
};

/**
 * This is the central handler for the scan filters state which is used in many components.
 * It gets/sets the scan filters as query-parameters and returns them as a javascript object.
 */
export const useScanFilters = (): ScanFilters => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastProjectIdRef = useRef<string | undefined>(undefined);

  const qaState = parseQAState(searchParams.get('qaState'));
  const scanCategory = parseScanCategory(searchParams.get('scanCategory'));

  const rawSurfaceClassifications = searchParams.get('surfaceClassifications');
  const surfaceClassifications = useMemo(() => {
    return parseSurfaceClassification(rawSurfaceClassifications);
  }, [rawSurfaceClassifications]);

  const hasViewport = searchParams.has('viewport');
  const rawViewport = searchParams.get('viewport');
  const viewport = useMemo(() => {
    return parseViewport(rawViewport);
  }, [rawViewport]);

  const rawFromDate = searchParams.get('fromDate');
  const rawToDate = searchParams.get('toDate');
  const rawDateRangeType = searchParams.get('dateRangeType');
  const dateRange = useMemo(() => {
    return parseDateRange(rawFromDate, rawToDate, rawDateRangeType) ?? defaultDateRange;
  }, [rawFromDate, rawToDate, rawDateRangeType]);

  const setQaState = useCallback(
    (newQaState: QAState) => {
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          if (newQaState) {
            next.set('qaState', newQaState);
          } else {
            next.delete('qaState');
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const removeQaState = useCallback(() => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.delete('qaState');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const setScanCategory = useCallback(
    (newScanCategory: scanCategoryApi.ScanCategoryKind) => {
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          next.set('scanCategory', scanCategoryApi.ScanCategoryKind[newScanCategory]);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const removeScanCategory = useCallback(() => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.delete('scanCategory');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const removeSurfaceClassification = useCallback(() => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.delete('surfaceClassifications');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const setSurfaceClassification = useCallback(
    (newSurfaceClassifications: SurfaceClassification[]) => {
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          next.set('surfaceClassifications', newSurfaceClassifications.join(','));
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setViewport = useCallback(
    (newViewport: BoundingBoxWithHeight) => {
      const stringifiedViewport = JSON.stringify(newViewport);
      if (searchParams.get('viewport') === stringifiedViewport) return;
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          next.delete('fromDate');
          next.delete('toDate');
          next.delete('dateRangeType');
          next.set('viewport', stringifiedViewport);
          return next;
        },
        { replace: false },
      );
    },
    [searchParams, setSearchParams],
  );

  const setDateRange = useCallback(
    ({ from, to, type }: DateRange) => {
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          next.delete('viewport');
          next.set('fromDate', from);
          next.set('toDate', to);
          next.set('dateRangeType', type);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const isInitialMount = lastProjectIdRef.current === undefined;
    const isProjectSwitch = !isInitialMount && lastProjectIdRef.current !== projectId;

    if (isInitialMount) {
      lastProjectIdRef.current = projectId;

      const hasSurfaceClassifications = searchParams.has('surfaceClassifications');
      const hasMapbox = searchParams.has('mapbox');
      const parsedViewport = parseViewport(searchParams.get('viewport'));
      const parsedFromDate = parseDate(searchParams.get('fromDate'));
      const parsedToDate = parseDate(searchParams.get('toDate'));
      const parsedDateRangeType = parseDateRangeType(searchParams.get('dateRangeType'));
      const hasValidTemporalFilter =
        parsedViewport || (parsedFromDate && parsedToDate && parsedDateRangeType) || hasMapbox;

      if (hasSurfaceClassifications && hasValidTemporalFilter) {
        return;
      }

      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          if (!hasSurfaceClassifications) {
            next.set('surfaceClassifications', surfaceClassificationCategories.join(','));
          }
          if (!hasValidTemporalFilter) {
            next.delete('viewport');
            next.set('fromDate', parsedFromDate ?? defaultDateRange.from);
            next.set('toDate', parsedToDate ?? defaultDateRange.to);
            next.set('dateRangeType', parsedDateRangeType ?? defaultDateRange.type);
          }
          return next;
        },
        { replace: true },
      );
    } else if (isProjectSwitch) {
      lastProjectIdRef.current = projectId;

      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          next.delete('viewport');
          next.delete('qaState');
          next.delete('scanCategory');
          next.set('fromDate', defaultDateRange.from);
          next.set('toDate', defaultDateRange.to);
          next.set('dateRangeType', defaultDateRange.type);
          next.set('surfaceClassifications', surfaceClassificationCategories.join(','));
          return next;
        },
        { replace: true },
      );
    }
  }, [projectId, searchParams, setSearchParams]);

  return useMemo(() => {
    const base = {
      qaState,
      setQaState,
      removeQaState,
      surfaceClassifications,
      setSurfaceClassification,
      removeSurfaceClassification,
      scanCategory,
      setScanCategory,
      removeScanCategory,
      setDateRange,
      setViewport,
    };

    if (hasViewport) {
      return {
        ...base,
        viewport: viewport!,
      } as ScanFilters;
    }

    return {
      ...base,
      dateRange,
    } as ScanFilters;
  }, [
    qaState,
    setQaState,
    removeQaState,
    surfaceClassifications,
    setSurfaceClassification,
    removeSurfaceClassification,
    scanCategory,
    setScanCategory,
    removeScanCategory,
    setDateRange,
    setViewport,
    hasViewport,
    viewport,
    dateRange,
  ]);
};
